// pages/notifications/NotificationCenter.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Badge,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Notifications as NotificationIcon,
  Search,
  MarkEmailRead,
  Delete,
  Star,
  StarBorder,
  PersonAdd,
  Edit,
  Restaurant,
  Category,
  Business,
  Store,
  Support,
  Newspaper,
  Schedule as ScheduleIcon,
  Refresh,
  CheckCircle
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../../../../hooks/useNotifications';

const NotificationCenter = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const MotionDiv = motion.div;
  
  const {
    notifications,
    unreadCount,
    loading,
    error,
    setError,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    formatNotificationTime,
    getNotificationColor
  } = useNotifications();
  
  // State management
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore] = useState(true);

  const getThemeColorFromPriority = (priority) => {
  switch (priority) {
    case 'critical': return 'error';
    case 'high': return 'warning'; 
    case 'medium': return 'info';
    case 'low': return 'success';
    default: return 'default';
  }
};
  
  // Tab configuration - memoized to prevent dependency changes
  const tabs = useMemo(() => [
    { label: t('notifications.tabs.all'), value: 'all', count: Array.isArray(notifications) ? notifications.length : 0 },
    { label: t('notifications.tabs.unread'), value: 'unread', count: unreadCount },
    { label: t('notifications.tabs.starred'), value: 'starred', count: Array.isArray(notifications) ? notifications.filter(n => n.is_starred).length : 0 },
    // **FIXED: Add null checks for category filtering**
    { label: t('notifications.tabs.userMgmt'), value: 'user_management', count: Array.isArray(notifications) ? notifications.filter(n => n.category === 'user_management').length : 0 },
    { label: t('notifications.tabs.content'), value: 'content_management', count: Array.isArray(notifications) ? notifications.filter(n => n.category === 'content_management').length : 0 },
    { label: t('notifications.tabs.vendor'), value: 'vendor_management', count: Array.isArray(notifications) ? notifications.filter(n => n.category === 'vendor_management').length : 0 }
  ], [t, notifications, unreadCount]);
  
  // Icon mapping for notification types
  const getNotificationIconComponent = (iconName, priority) => {
    const iconMap = {
      PersonAdd: PersonAdd,
      Edit: Edit,
      Delete: Delete,
      VpnKey: CheckCircle,
      Restaurant: Restaurant,
      Category: Category,
      Business: Business,
      Store: Store,
      Support: Support,
      Newspaper: Newspaper,
      Schedule: ScheduleIcon
    };
    
    const IconComponent = iconMap[iconName] || NotificationIcon;
    const color = getNotificationColor(priority);
    
    return (
      <Avatar
        sx={{
          bgcolor: `${color}.light`,
          color: `${color}.main`,
          width: 48,
          height: 48
        }}
      >
        <IconComponent />
      </Avatar>
    );
  };
  
  // Filter notifications based on current tab and filters
  // const filteredNotifications = useMemo(() => {
  //   // Ensure notifications is always an array
  //   if (!Array.isArray(notifications)) {
  //     return [];
  //   }
    
  //   let filtered = [...notifications];
    
  //   // Tab filtering
  //   switch (tabs[currentTab]?.value) {
  //     case 'unread':
  //       filtered = filtered.filter(n => !n.is_read && !n.is_dismissed);
  //       break;
  //     case 'starred':
  //       filtered = filtered.filter(n => n.is_starred);
  //       break;
  //     case 'user_management':
  //     case 'content_management':
  //     case 'vendor_management':
  //       filtered = filtered.filter(n => n.category === tabs[currentTab].value);
  //       break;
  //     default:
  //       // 'all' - no additional filtering
  //       break;
  //   }
    
  //   // Search filtering
  //   if (searchTerm) {
  //     const term = searchTerm.toLowerCase();
  //     filtered = filtered.filter(n => 
  //       n.title?.toLowerCase().includes(term) ||
  //       n.message?.toLowerCase().includes(term) ||
  //       n.actor_name?.toLowerCase().includes(term) ||
  //       n.target_name?.toLowerCase().includes(term)
  //     );
  //   }
    
  //   // Category filtering
  //   if (categoryFilter !== 'all') {
  //     filtered = filtered.filter(n => n.category === categoryFilter);
  //   }
    
  //   // Priority filtering
  //   if (priorityFilter !== 'all') {
  //     filtered = filtered.filter(n => n.priority === priorityFilter);
  //   }
    
  //   return filtered;
  // }, [notifications, currentTab, searchTerm, categoryFilter, priorityFilter, tabs]);

  const filteredNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    
    let filtered = notifications;
    
    // Tab filtering
    if (currentTab !== 0) {
      const tabValue = tabs[currentTab]?.value;
      if (tabValue === 'unread') {
        filtered = filtered.filter(n => !n.is_read);
      } else if (tabValue === 'starred') {
        filtered = filtered.filter(n => n.is_starred);
      } else if (tabValue && tabValue !== 'all') {
        // **FIXED: Add null check for category**
        filtered = filtered.filter(n => n.category === tabValue);
      }
    }
    
    // Search filtering
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(n => 
        (n.title?.toLowerCase().includes(term)) ||
        (n.message?.toLowerCase().includes(term)) ||
        (n.actor_name?.toLowerCase().includes(term)) ||
        (n.target_name?.toLowerCase().includes(term))
      );
    }
    
    // Category filtering
    if (categoryFilter !== 'all') {
      // **FIXED: Add null check for category**
      filtered = filtered.filter(n => n.category === categoryFilter);
    }
    
    // Priority filtering
    if (priorityFilter !== 'all') {
      // **FIXED: Add null check for priority**
      filtered = filtered.filter(n => n.priority === priorityFilter);
    }
    
    return filtered;
  }, [notifications, currentTab, searchTerm, categoryFilter, priorityFilter, tabs]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSelectedNotifications([]);
  };
  
  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };
  
  // Handle bulk actions
  const handleBulkMarkAsRead = async () => {
    for (const notificationId of selectedNotifications) {
      await markAsRead(notificationId);
    }
    setSelectedNotifications([]);
  };
  
  const handleBulkDismiss = async () => {
    for (const notificationId of selectedNotifications) {
      await dismissNotification(notificationId);
    }
    setSelectedNotifications([]);
  };
  
  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    
    // Navigate to action URL if available
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };
  
  // Load more notifications
  const loadMore = () => {
    const nextPage = page + 1;
    fetchNotifications({ 
      offset: nextPage * 20, 
      append: true,
      category: categoryFilter !== 'all' ? categoryFilter : null,
      priority: priorityFilter !== 'all' ? priorityFilter : null
    });
    setPage(nextPage);
  };
  
  // Refresh notifications
  const refreshNotifications = () => {
    setPage(0);
    fetchNotifications();
  };

  useEffect(() => {
  fetchNotifications();
}, [fetchNotifications]);

// Add useEffect to listen for real-time updates
useEffect(() => {
  const handleNewNotification = (event) => {
    console.log('🔔 New notification in center:', event.detail);
    // Refresh notifications when new ones arrive
    fetchNotifications();
  };

  const handleCategoryUpdate = (event) => {
    console.log('📂 Category updated, refreshing notifications:', event.detail);
    // Refresh notifications when categories are updated
    fetchNotifications();
  };

  window.addEventListener('new-notification', handleNewNotification);
  window.addEventListener('category-list-updated', handleCategoryUpdate);

  return () => {
    window.removeEventListener('new-notification', handleNewNotification);
    window.removeEventListener('category-list-updated', handleCategoryUpdate);
  };
}, [fetchNotifications]);
  
  return (
    <Box sx={{ pb: 6 }}>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              {t('notifications.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('notifications.subtitle')}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={refreshNotifications}
              disabled={loading}
            >
              {t('notifications.refresh')}
            </Button>
            
            {unreadCount > 0 && (
              <Button
                variant="contained"
                startIcon={<MarkEmailRead />}
                onClick={markAllAsRead}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                  }
                }}
              >
                {t('notifications.markAllRead')} ({unreadCount})
              </Button>
            )}
          </Stack>
        </Box>
      </MotionDiv>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                placeholder={t('notifications.searchPlaceholder')}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('notifications.category')}</InputLabel>
                <Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  label={t('notifications.category')}
                >
                  <MenuItem value="all">{t('notifications.allCategories')}</MenuItem>
                  <MenuItem value="user_management">{t('notifications.userManagement')}</MenuItem>
                  <MenuItem value="content_management">{t('notifications.contentManagement')}</MenuItem>
                  <MenuItem value="vendor_management">{t('notifications.vendorManagement')}</MenuItem>
                  <MenuItem value="system">{t('notifications.system')}</MenuItem>
                  <MenuItem value="support">{t('notifications.support')}</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('notifications.priority')}</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  label={t('notifications.priority')}
                >
                  <MenuItem value="all">{t('notifications.allPriorities')}</MenuItem>
                  <MenuItem value="critical">{t('notifications.critical')}</MenuItem>
                  <MenuItem value="high">{t('notifications.high')}</MenuItem>
                  <MenuItem value="medium">{t('notifications.medium')}</MenuItem>
                  <MenuItem value="low">{t('notifications.low')}</MenuItem>
                </Select>
              </FormControl>
              
              {selectedNotifications.length > 0 && (
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MarkEmailRead />}
                    onClick={handleBulkMarkAsRead}
                  >
                    {t('notifications.markAsRead')} ({selectedNotifications.length})
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={handleBulkDismiss}
                  >
                    {t('notifications.dismiss')} ({selectedNotifications.length})
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </MotionDiv>
      
      {/* Tabs */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {tab.label}
                      {tab.count > 0 && (
                        <Chip
                          label={tab.count}
                          size="small"
                          color={tab.value === 'unread' ? 'primary' : 'default'}
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          
          {/* Notification List */}
          <CardContent sx={{ p: 0 }}>
            {loading && page === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredNotifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <NotificationIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('notifications.noNotifications')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || categoryFilter !== 'all' || priorityFilter !== 'all'
                    ? t('notifications.noMatchingNotifications')
                    : t('notifications.allCaughtUp')}
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                <AnimatePresence>
                  {filteredNotifications.map((notification) => (
                    <MotionDiv
                      key={notification.notification_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ListItem
                        sx={{
                          borderLeft: !notification.is_read 
                            ? `4px solid ${theme.palette[getThemeColorFromPriority(notification.priority)]?.main || theme.palette.primary.main}` 
                            : 'none',
                          bgcolor: notification.is_read ? 'transparent' : 'action.hover',
                          '&:hover': {
                            bgcolor: 'action.selected'
                          },
                          cursor: 'pointer',
                          py: 2
                        }}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <ListItemAvatar>
                          {getNotificationIconComponent(notification.icon, notification.priority)}
                        </ListItemAvatar>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography
                                variant="subtitle1"
                                component="span" // ← FIXED: Use span
                                sx={{
                                  fontWeight: notification.is_read ? 400 : 600,
                                  flex: 1
                                }}
                              >
                                {notification.title}
                              </Typography>
                              
                              <Chip
                                label={notification.priority}
                                size="small"
                                color={getThemeColorFromPriority(notification.priority)}
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                              
                              {notification.priority === 'critical' && !notification.is_read && (
                                <Badge
                                  color="error"
                                  variant="dot"
                                  sx={{
                                    '& .MuiBadge-dot': {
                                      width: 10,
                                      height: 10
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box> {/* ← FIXED: Use Box instead of Typography wrapper */}
                              <Typography
                                component="span" // ← CRITICAL: Use span instead of default p tag
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1, display: 'block' }} // Use block display instead of p element
                              >
                                {notification.message}
                              </Typography>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography 
                                  component="span" // ← CRITICAL: Use span instead of default p tag
                                  variant="caption" 
                                  color="text.disabled"
                                >
                                  {formatNotificationTime(notification.created_at)}
                                  {notification.actor_name && ` • by ${notification.actor_name}`}
                                </Typography>
                                
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Chip
                                    label={notification.category ? notification.category.replace('_', ' ') : 'General'}
                                    size="small"
                                    variant="outlined"
                                    sx={{ 
                                      textTransform: 'capitalize',
                                      fontSize: '0.7rem',
                                      height: 20
                                    }}
                                  />
                                  
                                  <Chip
                                    label={notification.priority}
                                    size="small"
                                    color={getThemeColorFromPriority(notification.priority)}
                                    variant="outlined"
                                    sx={{ 
                                      textTransform: 'capitalize',
                                      fontSize: '0.7rem',
                                      height: 20
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle star toggle
                              }}
                            >
                              {notification.is_starred ? <Star color="warning" /> : <StarBorder />}
                            </IconButton>
                            
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                dismissNotification(notification.notification_id);
                              }}
                            >
                              <Delete />
                            </IconButton>
                          </Stack>
                        </ListItemSecondaryAction>
                      </ListItem>
                      
                      <Divider />
                    </MotionDiv>
                  ))}
                </AnimatePresence>
                
                {/* Load More Button */}
                {hasMore && filteredNotifications.length >= 20 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Button
                      variant="outlined"
                      onClick={loadMore}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={16} /> : undefined}
                    >
                      {loading ? t('notifications.loading') : t('notifications.loadMore')}
                    </Button>
                  </Box>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </MotionDiv>
    </Box>
  );
};

export default NotificationCenter;